import {
  createContext,
  type Dispatch,
  type SetStateAction,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { normalizePlanValue, type SubscriptionPlan } from './lib/subscriptionPlan';

const STORAGE_KEY = 'inktap:session:v3';

type Session = {
  // Hints from a session response; callers must verify fresh server authority.
  subjectId?: string;
  workspaceId?: string;
  email: string;
  slug: string | null;
  publicSlug: string | null;
  indexable: boolean;
  active: boolean;
  subscriptionPlan: SubscriptionPlan | null;
  subscriptionStatus: 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | null;
  expiresAt: string;
  canSyncToCloud: boolean;
  hasPhysicalCard: boolean;
  entitlements?: {
    canSyncToCloud: boolean;
    canManagePublicAlias: boolean;
    canControlIndexability: boolean;
    canUseBookingEmbeds: boolean;
    canUseAnalytics: boolean;
    canUseProtectedResources: boolean;
    canManagePhysicalCards: boolean;
    canDownloadProof: boolean;
  };
  limits?: {
    actionLimit: number | null;
    uploadStorageMb: number | null;
    analyticsLevel: 'none' | 'basic' | 'advanced' | 'security';
    supportLevel: 'standard' | 'priority';
  };
};

type SessionContextValue = {
  session: Session | null;
  setSession: Dispatch<SetStateAction<Session | null>>;
  replaceSession: (session: Session | null) => void;
};

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<Session | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }
    try {
      const parsed = JSON.parse(stored) as Partial<Session>;
      if (parsed && (typeof parsed.slug === 'string' || parsed.slug === null)) {
        const expiresAt = parsed.expiresAt ?? new Date(Date.now() + 3600_000).toISOString();
        if (Date.parse(expiresAt) <= Date.now()) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
        const identity = typeof parsed.subjectId === 'string' && parsed.subjectId.length > 0 &&
          typeof parsed.workspaceId === 'string' && parsed.workspaceId.length > 0
          ? { subjectId: parsed.subjectId, workspaceId: parsed.workspaceId } : {};
        const restoredSession: Session = {
          ...identity,
          email: parsed.email ?? '',
          slug: parsed.slug,
          publicSlug: parsed.publicSlug ?? null,
          indexable: Boolean(parsed.indexable),
          active: Boolean(parsed.active),
          subscriptionPlan: normalizePlanValue(parsed.subscriptionPlan) ?? null,
          subscriptionStatus:
            parsed.subscriptionStatus === 'ACTIVE' || parsed.subscriptionStatus === 'PAST_DUE' || parsed.subscriptionStatus === 'CANCELED'
              ? parsed.subscriptionStatus
              : null,
          expiresAt,
          canSyncToCloud: Boolean(parsed.canSyncToCloud),
          hasPhysicalCard: Boolean(parsed.hasPhysicalCard),
          entitlements: parsed.entitlements,
          limits: parsed.limits
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(restoredSession));
        return restoredSession;
      }
    } catch (error) {
      console.warn('Failed to parse stored session', error);
      return null;
    }
    return null;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [session]);

  const replaceSession = useCallback((nextSession: Session | null) => {
    if (typeof window !== 'undefined') {
      if (nextSession) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setSessionState(nextSession);
  }, []);

  const value = useMemo(
    () => ({
      session,
      setSession: setSessionState,
      replaceSession
    }),
    [replaceSession, session]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
}

export { SessionProvider, useSession };
export type { Session };
